import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Input,
  Gauge,
  ConfirmAlert,
  showAlert,
} from '../../../components';
import { useProposal } from '../../../context/proposalContext/ProposalContextProvider';
import { useProduct } from '../../../context/productContext/ProductContextProvider';
import {
  calculateMainProductGpRequired,
  calculatemainProductJaybelExcGstPrice,
  calculateMainProductCurrentSupplierGp,
  calculateMainProductSaving,
  calculateTotals,
  calculateMainProductJaybelIncGstPrice,
  calculatemainProductCurrentSupplierExcGstPrice,
  calculatemainProductCurrentSupplierIncGstPrice,
  calculateGpIfJaybelPriceGiven,
  calculateExclusiveJaybelPriceIfGpGiven,
} from '../../../utils/calculatePrices';
import AddAlternateProduct from './AddAlternateProduct';
import QuoteModel from '../../../model/proposal/QuoteProduct.model';
import ProductInfo from './ProductInfo';
import CreateAlternateProduct from '@/pages/setting/alternate-product/CreateAlternateProduct';
import {
  ModalProvider,
  useModal,
} from '@/components/common/modal/ModalContext';

const AddProduct = () => {
  // Handler for Jaybel Price Inc dropdown selection
  const handlePriceOptionChangeInc = (event) => {
    const optionValue = event.target.value;

    // this dropdown option should be treated as INCLUSIVE price
    const incFromOption =
      productForEdit?.mainProduct?.[`price${optionValue}exGst`];

    if (
      incFromOption === undefined ||
      incFromOption === null ||
      incFromOption === ''
    )
      return;

    const incPrice = Number(incFromOption); // selected option = INC
    const excPrice = (incPrice / 1.1).toFixed(2); // EX = INC / 1.1   << FIX

    const updatedProductForEdit = {
      ...productForEdit,
      mainProductJaybelIncGstPrice: incPrice,
      mainProductJaybelExcGstPrice: excPrice,
    };

    setProductForEdit(updatedProductForEdit);
    updateCalculations(updatedProductForEdit);
  };

  const {
    productForEdit,
    setProductForEdit,
    setIsProductSelected,
    setProposal,
    proposal,
    validateFields,
    gpError,
    setGpError,
    jaybelPriceError,
    setJaybelPriceError,
    jaybelIncGstPriceError,
    setJaybelIncGstPriceError,
    supplierPriceError,
    setSupplierPriceError,
    supplierIncGstPriceError,
    setSupplierIncGstPriceError,
    alternativeValue,
    setAlternativeValue,
    showAlternateProductSection,
    setShowAlternateProductSection,
    editProdIndex,
    setEditProdIndex,
    clearErrors,
  } = useProposal();

  const { openModal, closeModal } = useModal();
  const [selectedPriceOption, setSelectedPriceOption] = useState('');
  const [showExcPriceOptions, setShowExcPriceOptions] = useState(false);
  const [showIncPriceOptions, setShowIncPriceOptions] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const { getAllProduct } = useProduct();
  const [quantityError, setQuantityError] = useState('');

  const { initialState } = QuoteModel();
  // Handler for Current Customer Price Inc
  const handleCurrentSupplierIncGstPriceChange = (e) => {
    const value = e.target.value;
    // Autofill Exc GST price
    let mainProductCurrentSupplierExcGstPrice =
      value === '' ? '' : Number(value);

    if (productForEdit?.mainProduct?.taxCode !== 'E') {
      mainProductCurrentSupplierExcGstPrice =
        value === ''
          ? ''
          : calculatemainProductCurrentSupplierExcGstPrice(Number(value));
    }

    const updatedProductForEdit = {
      ...productForEdit,
      mainProductCurrentSupplierIncGstPrice: value === '' ? '' : Number(value),
      mainProductCurrentSupplierExcGstPrice,
    };
    setProductForEdit(updatedProductForEdit);
    updateCalculations(updatedProductForEdit);
  };

  // Handler for Jaybel Price Inc
  const handleJaybelIncGstPriceChange = (e) => {
    const value = e.target.value;
    const mainProductJaybelIncGstPrice = parseFloat(value);
    // Always calculate exclusive price if inclusive is entered
    let mainProductJaybelExcGstPrice =
      value === '' ? '' : mainProductJaybelIncGstPrice;

    if (productForEdit?.mainProduct?.taxCode !== 'E') {
      mainProductJaybelExcGstPrice =
        value === ''
          ? ''
          : calculatemainProductJaybelExcGstPrice(mainProductJaybelIncGstPrice);
    }

    // Recalculate GP using Jaybel and Supplier prices
    let mainProductGpRequired = '';
    const updatedProductForEdit = {
      ...productForEdit,
      mainProductJaybelIncGstPrice:
        value === '' ? '' : mainProductJaybelIncGstPrice,
      mainProductJaybelExcGstPrice:
        value === '' ? '' : mainProductJaybelExcGstPrice,
    };
    if (
      value !== '' &&
      !isNaN(mainProductJaybelExcGstPrice) &&
      productForEdit.mainProductCurrentSupplierExcGstPrice !== undefined &&
      productForEdit.mainProductCurrentSupplierExcGstPrice !== ''
    ) {
      mainProductGpRequired = calculateGpIfJaybelPriceGiven(
        Number(mainProductJaybelExcGstPrice),
        Number(updatedProductForEdit.mainProduct.avgCost),
      );
    }

    setProductForEdit((prev) => ({
      ...prev,
      mainProductJaybelIncGstPrice:
        value === '' ? '' : mainProductJaybelIncGstPrice,
      mainProductJaybelExcGstPrice:
        value === '' ? '' : mainProductJaybelExcGstPrice,
      mainProductGpRequired: isFinite(mainProductGpRequired)
        ? mainProductGpRequired
        : '',
    }));

    if (mainProductGpRequired > 100) {
      setGpError('GP Required cannot be more than 100%');
    } else {
      setGpError('');
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchProducts = async () => {
      if (searchQuery.length >= 3) {
        try {
          await getAllProduct(searchQuery);
          if (isMounted) {
            // Handle the response if needed
          }
        } catch (error) {
          if (isMounted) {
            // Handle error if needed
          }
        }
      }
    };
    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, [searchQuery]);

  const handleKeyDown = (event) => {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      '.',
    ];
    const isNumberKey = event.key >= '0' && event.key <= '9';
    if (!isNumberKey && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };
  const handleSave = () => {
    if (!validateFields()) {
      return;
    }

    // Ensure productForEdit includes alternateProducts and all GST-inclusive fields
    const productWithAlternates = {
      ...productForEdit,
      alternateProducts: productForEdit.alternateProducts,
      mainProductCurrentSupplierIncGstPrice:
        productForEdit.mainProductCurrentSupplierIncGstPrice === '' ||
        productForEdit.mainProductCurrentSupplierIncGstPrice === null
          ? null
          : Number(productForEdit.mainProductCurrentSupplierIncGstPrice),
      mainProductJaybelIncGstPrice:
        productForEdit.mainProductJaybelIncGstPrice === '' ||
        productForEdit.mainProductJaybelIncGstPrice === null
          ? null
          : Number(productForEdit.mainProductJaybelIncGstPrice),
      // Add alternate GST-inclusive totals explicitly
      alternateProductCurrentSupplierIncGstPrice:
        productForEdit.alternateProductCurrentSupplierIncGstPrice,
      alternateProductJaybelIncGstPrice:
        productForEdit.alternateProductJaybelIncGstPrice,
      alternateProductCurrentSupplierIncGstPriceTotal:
        productForEdit.alternateProductCurrentSupplierIncGstPriceTotal,
      alternateProductJaybelIncGstPriceTotal:
        productForEdit.alternateProductJaybelIncGstPriceTotal,
    };

    // Check if editProdIndex is not zero
    if (editProdIndex !== null) {
      // Update the existing product at the specified index
      setProposal((prevProposal) => {
        const updatedProducts = prevProposal.quoteProducts.map(
          (product, index) =>
            index === editProdIndex ? productWithAlternates : product,
        );

        return {
          ...prevProposal,
          quoteProducts: updatedProducts,
        };
      });
      showAlert('success', 'Product updated successfully');
    } else {
      // Add a new product
      setProposal((prevProposal) => ({
        ...prevProposal,
        quoteProducts: [...prevProposal.quoteProducts, productWithAlternates],
      }));
      showAlert('success', 'Product added successfully');
    }

    // Reset form state
    setProductForEdit({
      ...initialState,
      mainProductCurrentSupplierIncGstPrice: '',
      mainProductJaybelIncGstPrice: '',
    });
    setIsProductSelected(false); // Re-enable Typeahead
    setAlternativeValue('No');
    setShowAlternateProductSection(false);
    setEditProdIndex(null);

    //Here i want to target the typeahead
  };

  const handleAddNextProduct = async () => {
    await handleSave(); // Call handleSave
    const typeaheadInput = document.querySelector('#typeahead'); // Target Typeahead input by ID
    if (typeaheadInput) {
      typeaheadInput.focus(); // Focus the Typeahead input
    }
  };

  const openAlternateModal = (
    data = { mainProduct: {}, alternateProducts: [] },
  ) => {
    const mainProduct = productForEdit?.mainProduct || {};
    const alternateProducts = data.alternateProducts; // Ensure alternateProducts is an array

    openModal(
      <CreateAlternateProduct
        closeModal={closeModal}
        data={{ mainProduct, alternateProducts }} // Pass main product and alternate products
        isUpdate={true}
        setAlternativeValue={setAlternativeValue} // Pass the function
        setShowAlternateProductSection={setShowAlternateProductSection} // Pass the function
      />,
      'Add Alternate Product',
    );
  };

  useEffect(() => {
    if (!productForEdit?.alternateProduct) {
      setAlternativeValue('No'); // Reset alternative value if alternateProduct is undefined
      setShowAlternateProductSection(false);
    }
  }, [productForEdit?.alternateProduct]);

  const setAlternativeValueToNo = async () => {
    setShowAlternateProductSection(false);
    setAlternativeValue('No');

    const result = await ConfirmAlert({
      confirmBtnText: 'Add Alternate Product',
    });
    if (result.isConfirmed) {
      openAlternateModal({
        mainProduct: productForEdit.mainProduct,
        alternateProducts: [],
      }); // Pass empty array
    } else {
      setProductForEdit((prev) => ({
        ...prev,
        alternateProduct: null,
      }));
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (showAlternateProductSection === false) {
      setProductForEdit((prev) => {
        if (!isMounted) return prev;
        return {
          ...prev,
          alternateProduct: null,
          alternateProductQuotePrice: null,
          alternateProductJaybelExcGstPrice: null,
          alternateProductJaybelExcGstPriceTotal: null,
          alternateProductCurrentSupplierExcGstPrice: null,
          alternateProductCurrentSupplierGp: null,
          alternateProductCurrentSupplierExcGstTotal: null,
          alternateProductGpRequired: null,
          alternateProductSaving: null,
          alternateProductTotal: null,
        };
      });
    }
    return () => {
      isMounted = false;
    };
  }, [showAlternateProductSection]);

  // Auto-populate missing Inc GST fields when productForEdit is loaded or edited
  useEffect(() => {
    if (productForEdit?.mainProduct?.id) {
      const jaybelExc = productForEdit.mainProductJaybelExcGstPrice;
      const jaybelInc = productForEdit.mainProductJaybelIncGstPrice;
      const supplierExc = productForEdit.mainProductCurrentSupplierExcGstPrice;
      const supplierInc = productForEdit.mainProductCurrentSupplierIncGstPrice;

      const needsJaybelIncCalc =
        (jaybelInc === null || jaybelInc === undefined || jaybelInc === '') &&
        jaybelExc !== null &&
        jaybelExc !== undefined &&
        jaybelExc !== '';

      const needsSupplierIncCalc =
        (supplierInc === null || supplierInc === undefined || supplierInc === '') &&
        supplierExc !== null &&
        supplierExc !== undefined &&
        supplierExc !== '';

      if (needsJaybelIncCalc || needsSupplierIncCalc) {
        updateCalculations(productForEdit);
      }
    }
  }, [
    productForEdit?.mainProduct?.id,
    editProdIndex,
    productForEdit?.mainProductJaybelExcGstPrice,
    productForEdit?.mainProductCurrentSupplierExcGstPrice,
  ]);

  const handleAlternativeChange = (event) => {
    const value = event.target.value;
    setAlternativeValue(value);
    setShowAlternateProductSection(value === 'Yes');

    if (value === 'No') {
      setProductForEdit((prev) => ({
        ...prev,
        alternateProduct: null,
      }));
    }
  };

  // Update calculations, optionally skip GP auto-calc if user is editing GP
  const updateCalculations = (updatedProductForEdit, options = {}) => {
    const { skipGpAutoCalc = false } = options;
    // GST EXEMPT RULE: If taxCode === 'E' then Exc = Inc always
    if (updatedProductForEdit?.mainProduct?.taxCode === 'E') {
      updatedProductForEdit.mainProductCurrentSupplierIncGstPrice =
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== '' &&
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== null &&
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== undefined
          ? updatedProductForEdit.mainProductCurrentSupplierExcGstPrice
          : updatedProductForEdit.mainProductCurrentSupplierIncGstPrice;

      updatedProductForEdit.mainProductCurrentSupplierExcGstPrice =
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== '' &&
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== null &&
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== undefined
          ? updatedProductForEdit.mainProductCurrentSupplierIncGstPrice
          : updatedProductForEdit.mainProductCurrentSupplierExcGstPrice;

      updatedProductForEdit.mainProductJaybelIncGstPrice =
        updatedProductForEdit.mainProductJaybelExcGstPrice !== '' &&
        updatedProductForEdit.mainProductJaybelExcGstPrice !== null &&
        updatedProductForEdit.mainProductJaybelExcGstPrice !== undefined
          ? updatedProductForEdit.mainProductJaybelExcGstPrice
          : updatedProductForEdit.mainProductJaybelIncGstPrice;

      updatedProductForEdit.mainProductJaybelExcGstPrice =
        updatedProductForEdit.mainProductJaybelIncGstPrice !== '' &&
        updatedProductForEdit.mainProductJaybelIncGstPrice !== null &&
        updatedProductForEdit.mainProductJaybelIncGstPrice !== undefined
          ? updatedProductForEdit.mainProductJaybelIncGstPrice
          : updatedProductForEdit.mainProductJaybelExcGstPrice;
    } else {
      // Auto-calculate missing Inclusive or Exclusive values when taxCode !== 'E'
      if (
        (updatedProductForEdit.mainProductCurrentSupplierIncGstPrice === '' ||
          updatedProductForEdit.mainProductCurrentSupplierIncGstPrice === null ||
          updatedProductForEdit.mainProductCurrentSupplierIncGstPrice === undefined) &&
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== '' &&
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== null &&
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice !== undefined &&
        !isNaN(Number(updatedProductForEdit.mainProductCurrentSupplierExcGstPrice))
      ) {
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice =
          calculatemainProductCurrentSupplierIncGstPrice(
            Number(updatedProductForEdit.mainProductCurrentSupplierExcGstPrice),
          );
      } else if (
        (updatedProductForEdit.mainProductCurrentSupplierExcGstPrice === '' ||
          updatedProductForEdit.mainProductCurrentSupplierExcGstPrice === null ||
          updatedProductForEdit.mainProductCurrentSupplierExcGstPrice === undefined) &&
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== '' &&
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== null &&
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== undefined &&
        !isNaN(Number(updatedProductForEdit.mainProductCurrentSupplierIncGstPrice))
      ) {
        updatedProductForEdit.mainProductCurrentSupplierExcGstPrice =
          calculatemainProductCurrentSupplierExcGstPrice(
            Number(updatedProductForEdit.mainProductCurrentSupplierIncGstPrice),
          );
      }

      if (
        (updatedProductForEdit.mainProductJaybelIncGstPrice === '' ||
          updatedProductForEdit.mainProductJaybelIncGstPrice === null ||
          updatedProductForEdit.mainProductJaybelIncGstPrice === undefined) &&
        updatedProductForEdit.mainProductJaybelExcGstPrice !== '' &&
        updatedProductForEdit.mainProductJaybelExcGstPrice !== null &&
        updatedProductForEdit.mainProductJaybelExcGstPrice !== undefined &&
        !isNaN(Number(updatedProductForEdit.mainProductJaybelExcGstPrice))
      ) {
        updatedProductForEdit.mainProductJaybelIncGstPrice =
          calculateMainProductJaybelIncGstPrice(
            Number(updatedProductForEdit.mainProductJaybelExcGstPrice),
          );
      } else if (
        (updatedProductForEdit.mainProductJaybelExcGstPrice === '' ||
          updatedProductForEdit.mainProductJaybelExcGstPrice === null ||
          updatedProductForEdit.mainProductJaybelExcGstPrice === undefined) &&
        updatedProductForEdit.mainProductJaybelIncGstPrice !== '' &&
        updatedProductForEdit.mainProductJaybelIncGstPrice !== null &&
        updatedProductForEdit.mainProductJaybelIncGstPrice !== undefined &&
        !isNaN(Number(updatedProductForEdit.mainProductJaybelIncGstPrice))
      ) {
        updatedProductForEdit.mainProductJaybelExcGstPrice =
          calculatemainProductJaybelExcGstPrice(
            Number(updatedProductForEdit.mainProductJaybelIncGstPrice),
          );
      }
    }

    //Calculate GP using Jaybel and Supplier prices
    const jaybelExc = Number(
      updatedProductForEdit.mainProductJaybelExcGstPrice,
    );
    let mainProductGpRequired = '';
    if (!skipGpAutoCalc) {
      mainProductGpRequired = calculateGpIfJaybelPriceGiven(
        jaybelExc,
        updatedProductForEdit.mainProduct.avgCost,
      );
    } else {
      mainProductGpRequired = updatedProductForEdit.mainProductGpRequired;
    }

    const mainProductCurrentSupplierExcGstPrice =
      updatedProductForEdit.mainProductCurrentSupplierExcGstPrice;
    const mainProductCurrentSupplierGp = calculateMainProductCurrentSupplierGp(
      mainProductCurrentSupplierExcGstPrice,
      updatedProductForEdit.mainProduct.avgCost,
    );

    const mainProductSaving = calculateMainProductSaving(
      mainProductCurrentSupplierExcGstPrice,
      updatedProductForEdit.mainProductJaybelExcGstPrice,
    );
    const {
      mainProductJaybelExcGstPriceTotal,
      mainProductCurrentSupplierExcGstTotal,
      mainProductTotal,
    } = calculateTotals(
      updatedProductForEdit.mainProductQuantity || 0,
      updatedProductForEdit.mainProductJaybelExcGstPrice,
      mainProductCurrentSupplierExcGstPrice,
    );
    const mainProductQuotePrice =
      mainProductTotal / (updatedProductForEdit.mainProductQuantity || 1);

    // GST-inclusive totals logic
    let mainProductCurrentSupplierIncGstPriceTotal = null;
    let mainProductJaybelIncGstPriceTotal = null;
    // Current Supplier Inc GST
    mainProductCurrentSupplierIncGstPriceTotal =
      updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== null &&
      updatedProductForEdit.mainProductCurrentSupplierIncGstPrice !== ''
        ? Number(updatedProductForEdit.mainProductCurrentSupplierIncGstPrice) *
          (updatedProductForEdit.mainProductQuantity || 0)
        : isFinite(mainProductCurrentSupplierExcGstTotal) &&
            mainProductCurrentSupplierExcGstTotal !== '' &&
            mainProductCurrentSupplierExcGstTotal !== null
          ? Number(mainProductCurrentSupplierExcGstTotal) * 1.1
          : mainProductCurrentSupplierIncGstPriceTotal;

    // Jaybel Inc GST (ternary)
    mainProductJaybelIncGstPriceTotal =
      updatedProductForEdit.mainProductJaybelIncGstPrice !== null &&
      updatedProductForEdit.mainProductJaybelIncGstPrice !== ''
        ? Number(updatedProductForEdit.mainProductJaybelIncGstPrice) *
          (updatedProductForEdit.mainProductQuantity || 0)
        : isFinite(mainProductJaybelExcGstPriceTotal) &&
            mainProductJaybelExcGstPriceTotal !== '' &&
            mainProductJaybelExcGstPriceTotal !== null
          ? Number(mainProductJaybelExcGstPriceTotal) * 1.1
          : mainProductJaybelIncGstPriceTotal;

    setProductForEdit({
      ...updatedProductForEdit,
      mainProductCurrentSupplierExcGstPrice,
      mainProductCurrentSupplierGp: isFinite(mainProductCurrentSupplierGp)
        ? mainProductCurrentSupplierGp
        : '',
      mainProductSaving: isFinite(mainProductSaving) ? mainProductSaving : '',
      mainProductJaybelExcGstPriceTotal: isFinite(
        mainProductJaybelExcGstPriceTotal,
      )
        ? mainProductJaybelExcGstPriceTotal
        : '',
      mainProductCurrentSupplierExcGstTotal: isFinite(
        mainProductCurrentSupplierExcGstTotal,
      )
        ? mainProductCurrentSupplierExcGstTotal
        : '',
      mainProductTotal: isFinite(mainProductTotal) ? mainProductTotal : '',
      mainProductQuotePrice: isFinite(mainProductQuotePrice)
        ? mainProductQuotePrice
        : '',
      mainProductCurrentSupplierIncGstPrice:
        updatedProductForEdit.mainProductCurrentSupplierIncGstPrice ?? '',
      mainProductJaybelIncGstPrice:
        updatedProductForEdit.mainProductJaybelIncGstPrice ?? '',
      mainProductCurrentSupplierIncGstPriceTotal: isFinite(
        mainProductCurrentSupplierIncGstPriceTotal,
      )
        ? Number(mainProductCurrentSupplierIncGstPriceTotal.toFixed(2))
        : '',
      mainProductJaybelIncGstPriceTotal: isFinite(
        mainProductJaybelIncGstPriceTotal,
      )
        ? Number(mainProductJaybelIncGstPriceTotal.toFixed(2))
        : '',
      mainProductGpRequired: isFinite(mainProductGpRequired)
        ? mainProductGpRequired
        : '',
    });
  };

  const handleGpRequiredChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      const updatedProductForEdit = {
        ...productForEdit,
        mainProductGpRequired: '',
        mainProductJaybelExcGstPrice: '',
        mainProductJaybelIncGstPrice: '',
        mainProductCurrentSupplierIncGstPrice:
          productForEdit.mainProductCurrentSupplierIncGstPrice,
      };
      updateCalculations(updatedProductForEdit, { skipGpAutoCalc: true });
      setGpError('');
    } else {
      const mainProductGpRequired = parseFloat(value);
      if (!isNaN(mainProductGpRequired)) {
        if (mainProductGpRequired > 100) {
          setGpError('GP Required cannot be more than 100%');
        } else {
          // Calculate Jaybel exclusive price based on GP and supplier price
          const mainProductJaybelExcGstPrice =
            calculateExclusiveJaybelPriceIfGpGiven(
              Number(productForEdit.mainProduct.avgCost),
              mainProductGpRequired,
            );
          // Always calculate inclusive price if exclusive is calculated
          const mainProductJaybelIncGstPrice = isFinite(
            mainProductJaybelExcGstPrice,
          )
            ? calculateMainProductJaybelIncGstPrice(
                mainProductJaybelExcGstPrice,
              )
            : '';
          const updatedProductForEdit = {
            ...productForEdit,
            mainProductGpRequired: isFinite(mainProductGpRequired)
              ? mainProductGpRequired
              : '',
            mainProductJaybelExcGstPrice: isFinite(mainProductJaybelExcGstPrice)
              ? mainProductJaybelExcGstPrice
              : '',
            mainProductJaybelIncGstPrice,
            mainProductCurrentSupplierIncGstPrice:
              productForEdit.mainProductCurrentSupplierIncGstPrice,
          };
          updateCalculations(updatedProductForEdit, { skipGpAutoCalc: true });
          setGpError('');
        }
      }
    }
  };

  const handleJaybelPriceChange = (event) => {
    const value = event.target.value;
    const mainProductJaybelExcGstPrice = parseFloat(value);
    if (value === '' || !isNaN(mainProductJaybelExcGstPrice)) {
      // Always calculate inclusive price if exclusive is entered
      let mainProductJaybelIncGstPrice = value;

      if (productForEdit?.mainProduct?.taxCode !== 'E') {
        mainProductJaybelIncGstPrice =
          value === ''
            ? ''
            : calculateMainProductJaybelIncGstPrice(
                mainProductJaybelExcGstPrice,
              );
      }

      const updatedProductForEdit = {
        ...productForEdit,
        mainProductJaybelExcGstPrice: value,
        mainProductJaybelIncGstPrice:
          value === '' ? '' : mainProductJaybelIncGstPrice,
        mainProductCurrentSupplierIncGstPrice:
          productForEdit.mainProductCurrentSupplierIncGstPrice,
      };

      const mainProductGpRequired = calculateMainProductGpRequired(
        productForEdit.mainProduct.avgCost,
        mainProductJaybelExcGstPrice,
      );
      if (mainProductGpRequired > 100) {
        setGpError('GP Required cannot be more than 100%');
      } else {
        updatedProductForEdit.mainProductGpRequired = isFinite(
          mainProductGpRequired,
        )
          ? mainProductGpRequired
          : '';
        setGpError('');
      }
      setJaybelPriceError('');
      updateCalculations(updatedProductForEdit);
    }
  };

  const handleCurrentSupplierPriceChange = (event) => {
    const value = event.target.value;
    const mainProductCurrentSupplierExcGstPrice =
      value === '' ? '' : parseFloat(value);
    // Autofill Inc GST price
    let mainProductCurrentSupplierIncGstPrice = value;

    if (productForEdit?.mainProduct?.taxCode !== 'E') {
      mainProductCurrentSupplierIncGstPrice =
        value === ''
          ? ''
          : calculatemainProductCurrentSupplierIncGstPrice(Number(value));
    }
    if (value === '' || !isNaN(mainProductCurrentSupplierExcGstPrice)) {
      const updatedProductForEdit = {
        ...productForEdit,
        mainProductCurrentSupplierExcGstPrice: value,
        mainProductCurrentSupplierIncGstPrice,
        mainProductJaybelIncGstPrice:
          productForEdit.mainProductJaybelIncGstPrice,
      };
      setSupplierPriceError('');
      setProductForEdit(updatedProductForEdit);
      updateCalculations(updatedProductForEdit);
    }
  };

  const handleQuantityChange = (event) => {
    const value = event.target.value;
    const mainProductQuantity = value === '' ? '' : parseInt(value, 10);
    if (value === '' || !isNaN(mainProductQuantity)) {
      if (mainProductQuantity < 0) {
        setQuantityError('Quantity must be zero or greater');
      } else {
        setQuantityError('');
      }
      const updatedProductForEdit = {
        ...productForEdit,
        mainProductQuantity: isFinite(mainProductQuantity)
          ? mainProductQuantity
          : '',
        mainProductCurrentSupplierIncGstPrice:
          productForEdit.mainProductCurrentSupplierIncGstPrice,
        mainProductJaybelIncGstPrice:
          productForEdit.mainProductJaybelIncGstPrice,
      };
      setProductForEdit(updatedProductForEdit); // Ensure state is updated
      updateCalculations(updatedProductForEdit);
    }
  };

  const handleCommentChange = (event) => {
    const comment = event.target.value;
    setProductForEdit({
      ...productForEdit,
      comment,
    });
  };

  const handlePriceOptionChange = (event) => {
    const selectedOption = event.target.value;
    setSelectedPriceOption(selectedOption);
    if (selectedOption !== '') {
      const priceKey = `price${selectedOption}exGst`;
      const selectedPrice = productForEdit.mainProduct[priceKey];
      if (selectedPrice !== undefined) {
        const mainProductJaybelExcGstPrice = parseFloat(selectedPrice);
        const mainProductJaybelIncGstPrice = isFinite(
          mainProductJaybelExcGstPrice,
        )
          ? (mainProductJaybelExcGstPrice * 1.1).toFixed(2)
          : '';
        let mainProductGpRequired = '';
        if (
          productForEdit.mainProductCurrentSupplierExcGstPrice !== undefined &&
          productForEdit.mainProductCurrentSupplierExcGstPrice !== ''
        ) {
          mainProductGpRequired = calculateGpIfJaybelPriceGiven(
            Number(mainProductJaybelExcGstPrice),
            Number(productForEdit.mainProduct.avgCost),
          );
        }
        const updatedProductForEdit = {
          ...productForEdit,
          mainProductJaybelExcGstPrice: isFinite(mainProductJaybelExcGstPrice)
            ? mainProductJaybelExcGstPrice
            : '',
          mainProductJaybelIncGstPrice,
          mainProductGpRequired: isFinite(mainProductGpRequired)
            ? mainProductGpRequired
            : '',
        };
        setProductForEdit(updatedProductForEdit);
        updateCalculations(updatedProductForEdit);
      }
    }
  };

  const handleReset = async () => {
    setIsProductSelected(false); // Re-enable Typeahead
    setProductForEdit(initialState);
    setEditProdIndex(null);
    setProductForEdit(initialState);
    setIsProductSelected(false); // Re-enable Typeahead
    setAlternativeValue('No');
    setShowAlternateProductSection(false);
    setEditProdIndex(null);
    clearErrors();
  };

  return (
    <>
      <div className="p-5 shadow-lg rounded-xl bg-white z-0">
        {/* Header and Product Saving */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold mb-4">
            Product Information : {''}
            <span className="font-mono text-slate-700">
              {productForEdit?.mainProduct?.itemCode}{' '}
              <span className="text-sm">
                ( {productForEdit?.mainProduct?.itemDescription} )
              </span>
            </span>
          </h2>
          <div className="flex flex-col items-end text-right min-w-[120px]">
            <div>
              <Gauge value={productForEdit.mainProductSaving || 0} />
            </div>
            <div>Product Saving</div>
          </div>
        </div>

        {/* Product Info Section */}
        <div className="grid grid-cols-3 gap-2 m-2">
          <Input
            label="Cost"
            value={productForEdit?.mainProduct?.avgCost}
            disabled
            required
          />

          <Input
            label="Current Customer Price Exc"
            className=""
            value={productForEdit.mainProductCurrentSupplierExcGstPrice ?? ''}
            onChange={handleCurrentSupplierPriceChange}
            type="number"
            step="0.01"
            required
            error={supplierPriceError}
          />

          <Input
            label={
              <span className="text-red-400 font-semibold">
                Current Customer Price Inc
              </span>
            }
            value={productForEdit.mainProductCurrentSupplierIncGstPrice ?? ''}
            onChange={handleCurrentSupplierIncGstPriceChange}
            type="number"
            step="0.01"
            required
            //disabled={productForEdit?.mainProduct?.taxCode === 'E'}
            error={supplierIncGstPriceError}
          />

          <Input
            label="GP Required (%)"
            type="number"
            step="0.01"
            required
            value={productForEdit.mainProductGpRequired}
            onChange={handleGpRequiredChange}
            onKeyDown={handleKeyDown}
            error={gpError}
          />
          <div className="relative">
            <Input
              label="Jaybel Price Exc"
              type="number"
              step="0.01"
              required
              value={productForEdit.mainProductJaybelExcGstPrice}
              onChange={handleJaybelPriceChange}
              onFocus={() => setShowExcPriceOptions(true)}
              onBlur={() =>
                setTimeout(() => setShowExcPriceOptions(false), 200)
              }
              error={jaybelPriceError}
            />
            {showExcPriceOptions && (
              <div
                className="absolute z-10 bg-white border border-gray-300 rounded shadow-lg mt-1"
                onMouseDown={(e) => e.preventDefault()}>
                <ul className="list-none p-2">
                  {[
                    { value: '0', label: 'Price 0' },
                    { value: '1', label: 'Price 1' },
                    { value: '2', label: 'Price 2' },
                    { value: '3', label: 'Price 3' },
                    { value: '4', label: 'Price 4' },
                  ].map((option) => (
                    <li
                      key={option.value}
                      className="cursor-pointer p-1 hover:bg-gray-200 px-5"
                      onClick={() => {
                        handlePriceOptionChange({
                          target: { value: option.value },
                        });
                        setShowExcPriceOptions(false);
                      }}>
                      {option.label} -{' '}
                      {isFinite(
                        productForEdit?.mainProduct[
                          `price${option.value}exGst`
                        ],
                      )
                        ? productForEdit?.mainProduct[
                            `price${option.value}exGst`
                          ]
                        : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="relative">
            <Input
              label={
                <span className="text-red-400 font-semibold">
                  Jaybel Price Inc
                </span>
              }
              value={productForEdit.mainProductJaybelIncGstPrice || ''}
              onChange={handleJaybelIncGstPriceChange}
              type="number"
              step="0.01"
              required
              // disabled={productForEdit?.mainProduct?.taxCode === 'E'}
              error={jaybelIncGstPriceError}
              onFocus={() => setShowIncPriceOptions(true)}
              onBlur={() =>
                setTimeout(() => setShowIncPriceOptions(false), 200)
              }
            />

            {showIncPriceOptions && (
              <div
                className="absolute z-10 bg-white border border-gray-300 rounded shadow-lg mt-1"
                onMouseDown={(e) => e.preventDefault()}>
                <ul className="list-none p-2">
                  {[
                    { value: '0', label: 'Price 0' },
                    { value: '1', label: 'Price 1' },
                    { value: '2', label: 'Price 2' },
                    { value: '3', label: 'Price 3' },
                    { value: '4', label: 'Price 4' },
                  ].map((option) => (
                    <li
                      key={option.value}
                      className="cursor-pointer p-1 hover:bg-gray-200 px-5"
                      onClick={() => {
                        handlePriceOptionChangeInc({
                          target: { value: option.value },
                        });
                        setShowIncPriceOptions(false);
                      }}>
                      {option.label} -{' '}
                      {isFinite(
                        productForEdit?.mainProduct[
                          `price${option.value}exGst`
                        ],
                      )
                        ? productForEdit?.mainProduct[
                            `price${option.value}exGst`
                          ]?.toFixed(2)
                        : ''}
                      {/* If you actually store Inc prices directly, replace the above line with: */}
                      {/* productForEdit?.mainProduct[`price${option.value}IncGst`] */}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Input
            label="Supplier Gp (%)"
            value={productForEdit.mainProductCurrentSupplierGp}
            disabled
            required
          />

          <Input
            label="Quantity"
            required
            type="number"
            value={productForEdit.mainProductQuantity || ''}
            onChange={handleQuantityChange}
            error={quantityError}
          />
          <Input
            label="Comment"
            type="comment"
            value={productForEdit.comment || ''}
            onChange={handleCommentChange}
          />
          {/* <Input
          label="Unit Diviser"
          value={productForEdit?.mainProductUnitDiviser}
          onChange={(e) => {
            const updatedProductForEdit = {
              ...productForEdit,
              mainProductUnitDiviser: e.target.value,
            };
            setProductForEdit(updatedProductForEdit);
            updateCalculations(updatedProductForEdit);
          }}
        /> */}
        </div>

        <ProductInfo productForEdit={productForEdit} />

        {/* Buttons and Alternative Product */}
        <div className="flex items-center">
          <label className="mr-2 font-semibold">Alternative:</label>
          <label className="mr-4">
            <input
              type="radio"
              value="No"
              checked={alternativeValue === 'No'}
              onChange={handleAlternativeChange}
            />
            &nbsp; No
          </label>
          <label>
            <input
              type="radio"
              value="Yes"
              checked={alternativeValue === 'Yes'}
              onChange={handleAlternativeChange}
            />
            &nbsp; Yes
          </label>
        </div>

        {showAlternateProductSection && (
          <div className="">
            <AddAlternateProduct
              setAlternativeValueToNo={setAlternativeValueToNo}
              openAlternateModal={openAlternateModal}
            />
          </div>
        )}

        <div className="flex justify-end gap-4 mt-4">
          <Button onClick={handleSave}>
            {editProdIndex !== null ? 'Update' : 'Add Product'}
          </Button>

          <Button onClick={handleAddNextProduct}>Add Next Product</Button>

          <Button
            className="bg-red-500 text-white px-4 py-2 rounded-lg"
            onClick={handleReset}>
            Close
          </Button>
        </div>
      </div>
    </>
  );
};

const App = () => (
  <ModalProvider>
    <AddProduct />
  </ModalProvider>
);

export default App;
